/**
 * FileUpload - Component for uploading Excel files
 * author: Generated Component
 */
import React, { useState, useCallback, useRef } from 'react';
import * as XLSX from 'xlsx';
import { Alert, Spinner } from 'react-bootstrap';

const FileUpload = ({ onFileUpload, uploadedFile, t }) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const allowedTypes = [
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
    'application/vnd.ms-excel' // .xls
  ];

  const maxFileSize = 10 * 1024 * 1024; // 10MB

  const validateFile = (file) => {
    if (!allowedTypes.includes(file.type)) {
      throw new Error(t("FileUpload.errors.invalidType.label"));
    }
    if (file.size > maxFileSize) {
      throw new Error(t("FileUpload.errors.fileTooLarge.label"));
    }
    return true;
  };

  const parseExcelFile = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          
          const sheets = workbook.SheetNames.map(sheetName => {
            const worksheet = workbook.Sheets[sheetName];
            const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
            
            // Get column headers (first row)
            const headers = jsonData[0] || [];
            
            // Get data rows (skip header row)
            const dataRows = jsonData.slice(1);
            
            return {
              name: sheetName,
              columns: headers.map((header, index) => ({
                name: header || `Column ${index + 1}`,
                index: index,
                sampleData: dataRows.slice(0, 3).map(row => row[index]).filter(cell => cell !== undefined)
              })),
              rowCount: dataRows.length,
              columnCount: headers.length,
              data: dataRows
            };
          });
          
          resolve(sheets);
        } catch (error) {
          reject(new Error(t("FileUpload.errors.parseError.label")));
        }
      };
      
      reader.onerror = () => {
        reject(new Error(t("FileUpload.errors.readError.label")));
      };
      
      reader.readAsArrayBuffer(file);
    });
  };

  const handleFileSelect = useCallback(async (file) => {
    setError(null);
    setIsProcessing(true);
    
    try {
      validateFile(file);
      const sheets = await parseExcelFile(file);
      onFileUpload(file, sheets);
    } catch (error) {
      setError(error.message);
    } finally {
      setIsProcessing(false);
    }
  }, [onFileUpload]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  }, [handleFileSelect]);

  const handleFileInput = useCallback((e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileSelect(file);
    }
  }, [handleFileSelect]);

  const handleUploadClick = useCallback(() => {
    if (!isProcessing && !uploadedFile) {
      fileInputRef.current?.click();
    }
  }, [isProcessing, uploadedFile]);

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };



  return (
    <div data-testid="file-upload">
      {error && (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      )}

      {uploadedFile ? (
        <div className="card p-3 d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center gap-3">
            <div className="text-success" style={{ fontSize: '2.5rem' }}>
              <i className="fas fa-file-excel"></i>
            </div>
            <div>
              <h6 className="mb-1 fw-600">{uploadedFile.name}</h6>
              <p className="text-muted mb-1 small">{formatFileSize(uploadedFile.size)}</p>
              <p className="text-muted mb-0 small">{uploadedFile.type}</p>
            </div>
          </div>
          <button 
            className="btn btn-outline-primary btn-sm"
            onClick={() => {
              setError(null);
              onFileUpload(null, []);
            }}
          >
            {t("FileUpload.actions.changeFile.label")}
          </button>
        </div>
      ) : (
        <div 
          className={`border-dashed p-5 text-center bg-light ${isDragOver ? 'border-primary bg-primary bg-opacity-10' : 'border-secondary'}`}
          style={{ minHeight: '300px', cursor: 'pointer' }}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={handleUploadClick}
        >
          {isProcessing ? (
            <div>
              <Spinner animation="border" role="status" className="mb-3" style={{ width: '3rem', height: '3rem' }}>
                <span className="visually-hidden">{t("FileUpload.processing.label")}</span>
              </Spinner>
              <p className="fw-500">
                {t("FileUpload.processing.label")}
              </p>
            </div>
          ) : (
            <>
              <div className="text-muted mb-4" style={{ fontSize: '4rem' }}>
                <i className="fas fa-cloud-upload-alt"></i>
              </div>
              <h5 className="mb-2">{t("FileUpload.title.label")}</h5>
              <p className="text-muted mb-4">{t("FileUpload.subtitle.label")}</p>
              <div className="card mx-auto" style={{ maxWidth: '400px' }}>
                <div className="card-body">
                  <p className="fw-500 text-muted mb-2">
                    {t("FileUpload.requirements.label")}
                  </p>
                  <ul className="text-muted list-unstyled small mb-0">
                    <li>{t("FileUpload.requirementsExcelFiles.label")}</li>
                    <li>{t("FileUpload.requirementsMaxSize.label")}</li>
                  </ul>
                </div>
              </div>
              
              {/* Hidden file input */}
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileInput}
                data-testid="file-input"
                style={{ display: 'none' }}
              />
              
              {/* Visible upload button */}
              <button 
                type="button"
                className="btn btn-primary mt-4"
                onClick={(e) => {
                  e.stopPropagation();
                  fileInputRef.current?.click();
                }}
              >
                <i className="fas fa-upload me-2"></i>
                {t("FileUpload.actions.browse.label")}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default FileUpload; 